/**
 * Tenant-scoped persistence for zustand `persist` stores.
 *
 * ## The defect this exists to prevent
 *
 * Measured 2026-08-10 on a brand-new user's first sign-in, after a reset that
 * destroyed every local org: `sessionStorage["lexide:harn-tasks"]` and
 * `["lexide:missions"]` still held rows belonging to **an org that no longer
 * existed**, and the status bar reported them as hers — *"200 agente(s) · 500
 * tarefa(s)"* against a server that said 2.
 *
 * The severity is not the wrong number. It is that this state **survived a
 * sign-out AND a complete server-side wipe**, because it lives in a plane no
 * server-side delete can reach. The production shape of the same code path is
 * one lawyer seeing another firm's data after a re-login on a shared machine.
 *
 * ## Why a storage wrapper, and not a per-store `partialize`/`merge` edit
 *
 * The identity cannot go in the persist KEY: `persist` reads `name` once at
 * module load, long before auth resolves. So the stamp has to travel INSIDE the
 * payload, and the check has to happen on read.
 *
 * Doing that per store would mean editing every store's `partialize` and
 * `merge` — seven of them, each with its own snapshot shape, several of which
 * rebuild `Map`s in `merge`. One wrapper at the `storage` seam covers them all,
 * cannot be forgotten by a store that adds no `partialize`, and is testable
 * without mounting a single store.
 *
 * ## ⚖ D-95 — why this layer REFUSES when it cannot tell, and the affordance
 * layer PERMITS
 *
 * This repo has a written rule for permission affordances that says the
 * opposite of the rule here: *an affordance layer must PERMIT when it cannot
 * read.* Both are correct, and the discriminator is structural, not stylistic:
 *
 *     An affordance may fail OPEN because something below it fails CLOSED.
 *     A cache has nothing below it — it IS the answer.
 *
 * A permissive affordance shows a button whose action the server still refuses,
 * so a wrong guess costs a denied click. A permissive cache renders another
 * tenant's data with no layer beneath to catch the mistake. The test is not
 * "which way do we usually fail?" but **"is there an enforcing layer below
 * me?"** — here there is none, so an unreadable identity must discard.
 *
 * ⚠ **D-67 still holds inside that discard.** `peekTenantKey() === null` means
 * *"cannot tell yet"* (hydration routinely runs before auth resolves), NOT
 * *"denied"*. We drop the cached payload — the store simply starts from its
 * initial state and refetches — but nothing here logs or surfaces a rejection,
 * because a hydration race must not become a support ticket about missing
 * cases. Discarding a cache costs one refetch; that is the whole cost.
 */

import { createJSONStorage } from "zustand/middleware";
import type { PersistStorage } from "zustand/middleware";
import { peekTenantKey } from "@/lib/lexiun-jwt";

/** Sibling of `state`/`version` in the persisted envelope. */
const TENANT_FIELD = "__tenant";

/**
 * Every persist key this wrapper governs. Exported so the sign-out path can
 * clear exactly the same set it validates — two lists that drift are how a
 * "cleared on sign-out" claim becomes false for one key nobody re-checked.
 *
 * ⚠ Tenant-scoped ONLY. `lexide:ui` (theme/layout) and `lexide:acervo-lens`
 * (which lens the Acervo opens on) are deliberately absent: they carry no
 * tenant data, and wiping a user's theme on sign-out would be a regression
 * dressed as a security fix.
 */
export const TENANT_SCOPED_PERSIST_KEYS: readonly string[] = [
  "lexide:missions",
  "lexide:harn-tasks",
  "lexide:active-context",
  "lexide:case-files-open",
  "lexide:case-files-sort",
  "lexide:recent-copy-move-destinations",
  "lexide:context-pane",
  // Prompt history — lawyer-typed case text in localStorage; MUST clear
  // on sign-out. ⚠ Unlike its siblings this one is NOT a cache (no
  // server copy), which is why its store defers hydration until identity
  // resolves — see useComposerHistoryStore's skipHydration note.
  "lexide:composer-history",
];

/**
 * The three methods `createJSONStorage` actually calls. Deliberately NOT the
 * DOM `Storage` interface: two of the stores this wraps already pass zustand's
 * minimal `StateStorage` (a fail-soft sessionStorage shim, and a composite
 * local+session storage), neither of which implements `length`/`key`/`clear`.
 * Demanding the full DOM shape would have excluded exactly the two stores that
 * carry the most tenant data.
 */
export interface MinimalStorage {
  getItem(name: string): string | null;
  setItem(name: string, value: string): void;
  removeItem(name: string): void;
}

/**
 * What we accept as a base. Mirrors zustand's `StateStorage`, whose `getItem`
 * is async-CAPABLE (`string | Promise<string | null> | null`).
 *
 * ⚠ Every base we actually pass is synchronous (real `sessionStorage` /
 * `localStorage`, a fail-soft shim over them, a composite of both). A base that
 * returned a Promise could not be validated synchronously — and rather than
 * cast that possibility away, the read path REFUSES it. That is the D-95
 * direction: a cache we cannot check is a cache we must not serve.
 */
export interface AsyncCapableStorage {
  getItem(name: string): string | Promise<string | null> | null;
  setItem(name: string, value: string): unknown;
  removeItem(name: string): unknown;
}

/**
 * Wrap a storage so every write carries the current tenant stamp and every read
 * refuses a payload that is not provably this tenant's.
 *
 * `base` is a thunk because `sessionStorage`/`localStorage` must not be touched
 * at module-evaluation time (SSR/tests), and because callers already pass
 * fail-soft wrappers that decide lazily.
 */
export function createTenantScopedStorage(base: () => AsyncCapableStorage): MinimalStorage {
  return {
    getItem(name: string): string | null {
      let raw: string | Promise<string | null> | null;
      try {
        raw = base().getItem(name);
      } catch {
        return null;
      }
      if (!raw) return null;
      if (typeof raw !== "string") {
        // A thenable base cannot be validated synchronously. Refuse rather than
        // serve unchecked bytes — see the AsyncCapableStorage note.
        return null;
      }

      const current = peekTenantKey();
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Unparseable payload is not this tenant's by any evidence we have.
        return null;
      }

      const stamped =
        parsed && typeof parsed === "object"
          ? (parsed as Record<string, unknown>)[TENANT_FIELD]
          : undefined;

      // Refuse unless BOTH sides are known AND equal. This single condition
      // covers all three failure shapes deliberately:
      //   - `current === null`  → cannot tell (D-67 third state) → refuse
      //   - `stamped` missing   → written before stamping shipped → refuse
      //   - stamped !== current → another tenant, or another user in this org
      if (
        typeof stamped !== "string" ||
        current === null ||
        stamped !== current
      ) {
        // Drop it rather than leaving a payload that will be re-refused on
        // every read for the rest of the session. Best-effort: a storage that
        // refuses removal must not break hydration.
        try {
          base().removeItem(name);
        } catch {
          /* ignore */
        }
        return null;
      }
      return raw;
    },

    setItem(name: string, value: string): void {
      const current = peekTenantKey();
      let out = value;
      try {
        const parsed = JSON.parse(value) as Record<string, unknown>;
        // Stamp with whatever we know. An unknown identity writes `null`, which
        // the read path then refuses — a snapshot we cannot attribute is a
        // snapshot we must not hand back.
        parsed[TENANT_FIELD] = current;
        out = JSON.stringify(parsed);
      } catch {
        // Non-JSON payload: persist untouched rather than corrupting it. It
        // will fail the read check (no stamp) and be discarded, which is the
        // safe direction.
      }
      try {
        base().setItem(name, out);
      } catch {
        /* quota / private-mode: fail soft, exactly as before */
      }
    },

    removeItem(name: string): void {
      try {
        base().removeItem(name);
      } catch {
        /* ignore */
      }
    },

  };
}

/** `createJSONStorage` over a tenant-scoped raw storage — the form the stores use. */
export function createTenantScopedJSONStorage<T>(
  base: () => AsyncCapableStorage,
): PersistStorage<T> | undefined {
  return createJSONStorage<T>(() => createTenantScopedStorage(base));
}

/**
 * Drop every tenant-scoped cache. Called on sign-out.
 *
 * ⚠ This is HYGIENE, not the control. It cannot repair a browser that is
 * already poisoned — the payload measured in the incident was sitting in a tab
 * right then, and no future sign-out event fixes a cache that gets read on the
 * next load. The read-side check in `createTenantScopedStorage` is what makes a
 * foreign cache unreadable regardless of how it arrived. Both ship; only one is
 * load-bearing.
 */
export function clearTenantScopedCaches(): void {
  for (const key of TENANT_SCOPED_PERSIST_KEYS) {
    for (const store of [
      () => sessionStorage,
      () => localStorage,
    ] as const) {
      try {
        store().removeItem(key);
      } catch {
        /* ignore */
      }
    }
  }
  // `useContextPaneStore` splits across two suffixed keys of its own.
  for (const suffix of [":local", ":session"]) {
    for (const store of [
      () => sessionStorage,
      () => localStorage,
    ] as const) {
      try {
        store().removeItem(`lexide:context-pane${suffix}`);
      } catch {
        /* ignore */
      }
    }
  }
}
